import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPublicServices, getPublicProducts, getPublicProfile, checkPhoneDuplicate, getVkUserPublic, getBookingContext, type PublicProfile, type PublicProduct, type VkUserPublicDto, type BookingContextDto } from '../../shared/api/bookingApi.js';
import type { ServiceWithCategory } from '../../shared/api/bookingApi.js';
import type { ExternalContact, Service } from '@minibook/shared';
import { socialBrandIconCssColor } from '../../shared/socialBrand.js';
import { ApiError } from '../../shared/api/client.js';
import { AppIcon, type IconName } from '../../shared/ui/Icon';
import { applyTheme } from '../../shared/theme/themes.js';
import { devError, devLog } from '../../shared/lib/devLog.js';
import { UI_CONSTANTS } from '@minibook/shared';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import {
  formatPhone,
  normalizeNamePart,
  isNamePartValid,
  buildExternalContactHref,
  getExternalContactDisplayLabel,
  getExternalContactPlatformMeta,
  isValidPhone,
  normalizePhone,
  getDefaultCountryForLanguage,
} from '@minibook/shared';
import { getSlugFromHost, getSlugFromQuery } from '../../shared/bookingHost';
import { getPublicSiteStage } from '../../shared/config/siteStage.js';
import { getPriceLocale } from '../../shared/formatting.js';
import { parseVkUserId } from '../../shared/booking/bookingSlotUtils.js';
import { useBookingAvailability } from '../../shared/booking/hooks/useBookingAvailability.js';
import { useBookingSubmit } from '../../shared/booking/hooks/useBookingSubmit.js';
import { BookingStep2Time } from '../../shared/booking/components/BookingStep2Time.js';
import { BookingStep3Client } from '../../shared/booking/components/BookingStep3Client.js';
import { BookingStep1Services } from '../../shared/booking/components/BookingStep1Services.js';
import { BookingProductsPage } from '../../shared/booking/components/BookingProductsPage.js';
import { BookingSummaryBar } from '../../shared/booking/components/BookingSummaryBar.js';
import { BookingRateLimitModal } from '../../shared/booking/components/BookingRateLimitModal.js';
import { ALL_CATEGORY_KEY } from '../../shared/booking/bookingConstants.js';
import type { BookingStep } from '../../shared/booking/bookingConstants.js';
import '../styles/booking.css';

// Кэшируем cover-фото мастера, чтобы не мигали на публичной странице
const coverPhotoCache = new Map<string, Promise<string | null>>();

export default function BookingPage() {
  const slugFromParams = useParams<{ slug: string }>().slug;
  const slugFromHost = getSlugFromHost();
  const slugFromQuery = getSlugFromQuery();
  const slug = slugFromParams ?? slugFromHost ?? slugFromQuery ?? null;
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [themeReady, setThemeReady] = useState(false);

  // Полный путь /booking/5472 → короткий URL в адресной строке (поддомен: /, localhost: /?code=5472)
  useEffect(() => {
    if (!slugFromParams || !window.location.pathname.startsWith('/booking/')) return;
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (slugFromHost) {
      const shortUrl = `${window.location.origin}/`;
      if (window.location.href !== shortUrl) {
        window.history.replaceState(null, '', shortUrl);
        navigate('/', { replace: true });
      }
    } else if (isLocalhost) {
      const shortUrl = `${window.location.origin}/?code=${slugFromParams}`;
      if (window.location.href !== shortUrl) {
        window.history.replaceState(null, '', shortUrl);
        navigate(`/?code=${slugFromParams}`, { replace: true });
      }
    }
  }, [slugFromHost, slugFromParams, navigate]);

  const [step, setStep] = useState<BookingStep>(1);
  const [currentPage, setCurrentPage] = useState<0 | 1 | 2 | 3 | 5>(0); // 0 = лендинг, 1–3 = шаги, 5 = товары
  const [previousPage, setPreviousPage] = useState<0 | 1 | 2 | 3 | 5>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileNotFound, setProfileNotFound] = useState(false);
  const [phoneWarning, setPhoneWarning] = useState<string | null>(null);
  const phoneCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** services-first: сначала услуги, потом дата/время. date-first: сначала дата/время, потом услуги с фильтром по maxDurationMin */
  const [bookingFlowMode, setBookingFlowMode] = useState<'services-first' | 'date-first'>('services-first');

  // Шаг 1: Услуги
  const [services, setServices] = useState<ServiceWithCategory[]>([]);
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(ALL_CATEGORY_KEY);

  // Товары (опционально)
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [selectedProductCategory, setSelectedProductCategory] = useState<string>(ALL_CATEGORY_KEY);

  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const priceLocale = getPriceLocale(language);

  const {
    selectedDate,
    setSelectedDate,
    selectedSlot,
    setSelectedSlot,
    availableSlots,
    slotsLoading,
    availableDates,
    occupancyByDate,
    calendarLoading,
    calendarError,
    calendarRequestId,
    calendarRevealed,
    today,
    maxDate,
    groupedSlots,
    formatSlotTime,
    handleDateSelect,
    handleSlotSelect,
    handleCalendarMonthChange,
    resetAvailabilityCaches,
    selectedSlotMaxMinutes,
  } = useBookingAvailability({
    slug,
    masterTimeZone: profile?.timeZone,
    language,
    t,
    selectedServices,
    currentPage,
    step,
    setError,
  });

  const clearAllSelections = useCallback(() => {
    setSelectedServices([]);
    setSelectedProducts([]);
    resetAvailabilityCaches();
    setError(null);
  }, [resetAvailabilityCaches]);

  const summaryButtonLabel =
    selectedSlot && (selectedServices.length > 0 || bookingFlowMode !== 'date-first')
      ? t('booking.continue')
      : bookingFlowMode === 'date-first' && selectedSlot
        ? t('booking.selectService')
        : t('booking.selectDateTime');
  const totalDuration = selectedServices.reduce((sum, s) => sum + s.durationMin, 0);
  const totalPrice = selectedServices.reduce((sum, s) => sum + Number(s.price), 0);
  const totalProductsPrice = products
    .filter((p) => selectedProducts.includes(p.id))
    .reduce((sum, p) => sum + Number(p.price), 0);
  const totalWithProducts = totalPrice + totalProductsPrice;
  const remainingMinutes =
    selectedSlotMaxMinutes != null ? selectedSlotMaxMinutes - totalDuration : null;

  const handleSummaryClick = () => {
    if (selectedSlot) {
      if (bookingFlowMode === 'date-first' && selectedServices.length === 0 && selectedProducts.length === 0) {
        setPreviousPage(currentPage);
        setCurrentPage(1);
        setStep(1);
      } else {
        setPreviousPage(currentPage);
        setStep(3);
      }
    } else {
      setPreviousPage(currentPage);
      setCurrentPage(2);
      setStep(2);
    }
    setError(null);
  };

  const hasProducts = products.length > 0;

  // Cleanup для debounce таймера при размонтировании
  useEffect(() => {
    return () => {
      if (phoneCheckTimeoutRef.current) {
        clearTimeout(phoneCheckTimeoutRef.current);
      }
    };
  }, []);

  // Шаг 3: Данные клиента (структура как в приложении мастера: Новый клиент)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [middleName, _setMiddleName] = useState('');
  const [clientPhoneE164, setClientPhoneE164] = useState('');
  const [bookingPhoneTouched, setBookingPhoneTouched] = useState(false);
  const bookingPhoneCountry = useMemo(() => getDefaultCountryForLanguage(language), [language]);
  const [clientEmail, setClientEmail] = useState('');
  const [comment, setComment] = useState('');
  const [vkId, setVkId] = useState('');
  const notifyChannel = 'email' as const;
  const [bookingContext, setBookingContext] = useState<BookingContextDto | null>(null);
  const [, setBookingContextLoading] = useState(false);
  const [clientGender, setClientGender] = useState<'male' | 'female' | ''>('');
  const [clientBirthDate, setClientBirthDate] = useState<{ day: string; month: string; year: string }>({ day: '', month: '', year: '' });
  const [loadingVkData, setLoadingVkData] = useState(false);
  const [vkPhoto, setVkPhoto] = useState<string | null>(null);
  const vkFetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const step3FieldsRef = useRef({ firstName: '', lastName: '', middleName: '', vkId: '', clientGender: '' as '' | 'male' | 'female', clientBirthDate: { day: '', month: '', year: '' } });
  step3FieldsRef.current = { firstName, lastName, middleName, vkId, clientGender: clientGender as '' | 'male' | 'female', clientBirthDate };
  const lastLockedEmailRef = useRef<string | null>(null);
  const clientEmailRef = useRef('');
  clientEmailRef.current = clientEmail;
  const lockedClientEmail = bookingContext?.clientEmail ?? null;
  const effectiveClientEmail = lockedClientEmail ?? clientEmail;

  const {
    submitting,
    nameError,
    setNameError,
    emailError,
    setEmailError,
    rateLimitRetryAfter,
    setRateLimitRetryAfter,
    handleSubmit: submitBooking,
    handleRetry: retryBooking,
  } = useBookingSubmit({
    slug,
    navigate,
    t,
    selectedServices,
    selectedProducts,
    selectedSlot,
    effectiveClientEmail,
    firstName,
    lastName,
    middleName,
    clientPhoneE164,
    clientGender,
    clientBirthDate,
    comment,
    vkId,
    notifyChannel,
  });

  const handleSubmit = (e: React.FormEvent) => submitBooking(e, setError);
  const handleRetry = () => retryBooking(setError);

  const goToServices = useCallback(() => {
    setCurrentPage(1);
    setStep(1);
    setError(null);
  }, []);

  const goToProducts = useCallback(() => {
    setCurrentPage(5);
    setError(null);
  }, []);

  const goToDateTime = useCallback(() => {
    setCurrentPage(2);
    setStep(2);
    if (!selectedDate) setSelectedDate(today);
    setError(null);
  }, [selectedDate, setSelectedDate, today]);

  const handleClientNameBlur = useCallback(() => {
    const nextFirst = normalizeNamePart(firstName);
    const nextLast = normalizeNamePart(lastName);
    if (nextFirst !== firstName) setFirstName(nextFirst);
    if (nextLast !== lastName) setLastName(nextLast);
    if (!nextFirst && !nextLast) {
      setNameError(t('booking.client.nameOrSurnameRequired'));
      return;
    }
    if (nextFirst && !isNamePartValid(nextFirst)) {
      setNameError(t('booking.client.nameCharsOnly'));
      return;
    }
    if (nextLast && !isNamePartValid(nextLast)) {
      setNameError(t('booking.client.nameCharsOnly'));
      return;
    }
    setNameError(null);
  }, [firstName, lastName, setNameError, t]);

  const handleClientEmailBlur = useCallback(() => {
    const v = effectiveClientEmail.trim();
    if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setEmailError(t('booking.client.emailInvalidOptional'));
    } else {
      setEmailError(null);
    }
  }, [effectiveClientEmail, setEmailError, t]);

  const handleVkIdChange = useCallback((value: string) => {
    setVkId(value);
    setVkPhoto(null);
  }, []);

  const summaryBar = (
    <BookingSummaryBar
      t={t}
      currentPage={currentPage}
      step={step}
      priceLocale={priceLocale}
      selectedServices={selectedServices}
      selectedProducts={selectedProducts}
      selectedSlot={selectedSlot}
      totalDuration={totalDuration}
      totalWithProducts={totalWithProducts}
      hasProducts={hasProducts}
      summaryButtonLabel={summaryButtonLabel}
      loading={loading}
      submitting={submitting}
      rateLimitRetryAfter={rateLimitRetryAfter}
      formatSlotTime={formatSlotTime}
      onContinue={handleSummaryClick}
      onGoToServices={goToServices}
      onGoToProducts={goToProducts}
      onGoToDateTime={goToDateTime}
    />
  );

  useEffect(() => {
    if (!clientPhoneE164.trim()) setBookingPhoneTouched(false);
  }, [clientPhoneE164]);

  // Загрузка контекста по телефону: email можно залочить, если он уже сохранён у клиента.
  const phoneForContext = useMemo(() => {
    const p = clientPhoneE164.trim();
    return isValidPhone(p) ? normalizePhone(p) : null;
  }, [clientPhoneE164]);
  useEffect(() => {
    if (!slug || !phoneForContext) {
      if (lastLockedEmailRef.current && clientEmailRef.current === lastLockedEmailRef.current) {
        setClientEmail('');
      }
      lastLockedEmailRef.current = null;
      setBookingContext(null);
      return;
    }
    let cancelled = false;
    setBookingContextLoading(true);
    getBookingContext(slug, phoneForContext)
      .then((ctx) => {
        if (cancelled) return;
        setBookingContext(ctx);
        if (ctx.clientEmail) {
          setClientEmail(ctx.clientEmail);
          lastLockedEmailRef.current = ctx.clientEmail;
          setEmailError(null);
        } else if (lastLockedEmailRef.current && clientEmailRef.current === lastLockedEmailRef.current) {
          setClientEmail('');
          lastLockedEmailRef.current = null;
        }
      })
      .catch(() => {
        if (!cancelled) {
          if (lastLockedEmailRef.current && clientEmailRef.current === lastLockedEmailRef.current) {
            setClientEmail('');
          }
          lastLockedEmailRef.current = null;
          setBookingContext({ connectedChannels: ['email'], preferredChannel: null, clientEmail: null });
        }
      })
      .finally(() => {
        if (!cancelled) setBookingContextLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug, phoneForContext]);

  // Принцип: при вводе данных (телефон, VK ID) проверяем базу мастера только на факт существования,
  // а данные в форму подставляем только из пользовательского ввода и VK public lookup.

  const applyVkDataToForm = useCallback((data: VkUserPublicDto) => {
    setVkPhoto(data.photo ?? null);
    setFirstName(data.firstName ?? '');
    setLastName(data.lastName ?? '');
    setClientGender(data.sex === 2 ? 'male' : data.sex === 1 ? 'female' : '');
    if (data.birthDate && data.birthDate.trim()) {
      const parts = data.birthDate.trim().split('.');
      if (parts.length >= 2) {
        setClientBirthDate({
          day: parts[0].padStart(2, '0'),
          month: parts[1].padStart(2, '0'),
          year: parts.length >= 3 ? parts[2].trim() : '',
        });
      } else {
        setClientBirthDate({ day: '', month: '', year: '' });
      }
    } else {
      setClientBirthDate({ day: '', month: '', year: '' });
    }
  }, []);

  const fetchDataByVkId = useCallback((s: string, vkUserId: string) => {
    setLoadingVkData(true);
    getVkUserPublic(s, vkUserId)
      .then((data) => applyVkDataToForm(data ?? {}))
      .catch(() => applyVkDataToForm({}))
      .finally(() => setLoadingVkData(false));
  }, [applyVkDataToForm]);

  useEffect(() => {
    if (!slug) return;
    const vkUserId = parseVkUserId(vkId);
    if (!vkUserId) {
      setVkPhoto(null);
      return;
    }
    if (vkFetchTimeoutRef.current) clearTimeout(vkFetchTimeoutRef.current);
    vkFetchTimeoutRef.current = setTimeout(() => {
      vkFetchTimeoutRef.current = null;
      fetchDataByVkId(slug, vkUserId);
    }, 500);
    return () => { if (vkFetchTimeoutRef.current) clearTimeout(vkFetchTimeoutRef.current); };
  }, [slug, vkId, fetchDataByVkId]);

  const handleFetchVkData = useCallback(() => {
    if (!slug) return;
    const vkUserId = parseVkUserId(vkId);
    if (!vkUserId) return;
    fetchDataByVkId(slug, vkUserId);
  }, [slug, vkId, fetchDataByVkId]);

  /** Эвристика: разнести «Имя Фамилия» из одного поля по полям Имя / Фамилия (не блокирует submit). */
  const handleSplitName = useCallback(() => {
    const tokens = firstName.trim().split(/\s+/).filter(Boolean);
    if (tokens.length >= 2) {
      setFirstName(tokens[0]!);
      setLastName(tokens.slice(1).join(' '));
      setNameError(null);
    }
  }, [firstName]);

  // Проверка дубликата телефона при наборе полного номера (debounce 500ms)
  useEffect(() => {
    if (!isValidPhone(clientPhoneE164.trim()) || !slug) {
      setPhoneWarning(null);
      return;
    }
    if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);
    phoneCheckTimeoutRef.current = setTimeout(() => {
      phoneCheckTimeoutRef.current = null;
      checkPhoneDuplicate(slug, normalizePhone(clientPhoneE164.trim()))
        .then((data) => {
          if (data.exists) {
            setPhoneWarning(t('booking.client.phoneExistsInDb'));
          } else {
            setPhoneWarning(null);
          }
        })
        .catch(() => setPhoneWarning(null));
    }, 500);
    return () => {
      if (phoneCheckTimeoutRef.current) {
        clearTimeout(phoneCheckTimeoutRef.current);
        phoneCheckTimeoutRef.current = null;
      }
    };
  }, [slug, clientPhoneE164, t]);

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [coverReady, setCoverReady] = useState(false);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);

  // Загрузка профиля и услуг при монтировании
  useEffect(() => {
    if (!slug) return;

    setLoading(true);
    setError(null);
    setProfileNotFound(false);
    setThemeReady(false);

    (async () => {
      try {
        let profileData: PublicProfile | null = null;
        try {
          profileData = await getPublicProfile(slug);
        } catch (err: any) {
          if (err?.error?.code === 'not_found') {
            setProfileNotFound(true);
            setError(null);
            applyTheme('light');
            setThemeReady(true);
            setServices([]);
            return;
          }
          throw err;
        }

        if (profileData) {
          setProfileNotFound(false);
          const theme = profileData.theme && ['light', 'dark', 'pink', 'olive'].includes(profileData.theme)
            ? profileData.theme as 'light' | 'dark' | 'pink' | 'olive'
            : 'light';
          if (import.meta.env.DEV) {
            devLog('[BookingPage] Применяем тему из профиля:', { theme, profileTheme: profileData.theme, fullProfile: profileData });
          }
          applyTheme(theme);
          // Считаем выключенной только при ЯВНОМ false.
          // Старые/разные бэкенды могут не присылать onlineBookingEnabled, но присылать enabled (или ничего).
          const onlineBookingEnabled =
            (typeof profileData.onlineBookingEnabled === 'boolean' ? profileData.onlineBookingEnabled : undefined) ??
            (typeof profileData.enabled === 'boolean' ? profileData.enabled : undefined) ??
            true;
          const isDisabled = onlineBookingEnabled === false;
          setProfile({
            ...profileData,
            onlineBookingEnabled,
            enabled: profileData.enabled,
          });
          if (isDisabled) {
            setServices([]);
            setThemeReady(true);
            return;
          }
          setThemeReady(true);
        }

        const servicesData = await getPublicServices(slug).catch((err: ApiError | Error) => {
          devError('[BookingPage] Ошибка загрузки услуг:', err);
          if ('error' in err && err.error?.code === 'not_found') {
            throw new Error(t('booking.error.noServices'));
          }
          throw err;
        });
        setServices(servicesData || []);
        const productsData = await getPublicProducts(slug).catch(() => []);
        setProducts(Array.isArray(productsData) ? productsData : []);
      } catch (err: any) {
        devError('[BookingPage] Ошибка загрузки данных:', err);
        const errorMessage = err?.error?.message || (err instanceof Error ? err.message : t('booking.error.loadData'));
        setError(errorMessage || t('booking.error.loadData'));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Прогрев и показ cover-фото без видимых загрузок
  useEffect(() => {
    const url = profile?.coverPhoto?.trim();
    setCoverReady(false);
    setCoverUrl(null);
    if (!url) return;

    let mounted = true;
    let promise = coverPhotoCache.get(url);
    if (!promise) {
      promise = new Promise<string | null>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => resolve(null);
        img.src = url;
      });
      coverPhotoCache.set(url, promise);
    }

    promise.then((readyUrl) => {
      if (!mounted) return;
      if (readyUrl) {
        setCoverUrl(readyUrl);
        setCoverReady(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, [profile?.coverPhoto]);

  const toggleService = (service: Service) => {
    if (selectedServices.some((s) => s.id === service.id)) {
      setSelectedServices(selectedServices.filter((s) => s.id !== service.id));
    } else {
      if (selectedSlotMaxMinutes != null && totalDuration + service.durationMin > selectedSlotMaxMinutes) {
        setError(t('booking.error.serviceDoesNotFit'));
        return;
      }
      setSelectedServices([...selectedServices, service]);
    }
    setError(null);
  };

  const contactLinks = useMemo(() => {
    const isRuContour = getPublicSiteStage() === 'ru';
    const rawItems = [...(profile?.socialLinks || []), ...(profile?.messengerLinks || [])];
    const items = isRuContour ? rawItems : rawItems.filter((c) => c.platform !== 'max');
    const fallback: ExternalContact[] = [];
    if (items.length === 0) {
      if (profile?.vkUrl) fallback.push({ platform: 'vk', value: profile.vkUrl });
      if (profile?.tgUsername) fallback.push({ platform: 'telegram', value: `@${profile.tgUsername.replace(/^@+/, '')}` });
      if (isRuContour && profile?.max) fallback.push({ platform: 'max', value: profile.max });
    }
    return (items.length > 0 ? items : fallback)
      .map((contact) => ({
        contact,
        href: buildExternalContactHref(contact),
        label: getExternalContactDisplayLabel(contact),
        icon: getExternalContactPlatformMeta(contact.platform).icon as IconName,
      }))
      .filter((item) => !!item.href);
  }, [profile?.max, profile?.messengerLinks, profile?.socialLinks, profile?.tgUsername, profile?.vkUrl]);

  // Категории и фильтр услуг — вызываем до любых early return (правила хуков)
  const noCategoryLabel = t('booking.noCategory');
  const categories = [...new Set(services.map((s) => s.category?.name || noCategoryLabel))];
  const filteredServices = services.filter((service) => {
    const matchesSearch =
      !searchQuery ||
      service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (service.description && service.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const categoryName = service.category?.name || noCategoryLabel;
    const matchesCategory = selectedCategory === ALL_CATEGORY_KEY || categoryName === selectedCategory;
    const fitsMaxDuration =
      selectedSlotMaxMinutes == null ||
      (remainingMinutes != null && service.durationMin <= remainingMinutes);
    return matchesSearch && matchesCategory && fitsMaxDuration;
  });
  const canAddService = useCallback(
    (service: Service) =>
      selectedSlotMaxMinutes == null ||
      totalDuration + service.durationMin <= selectedSlotMaxMinutes,
    [selectedSlotMaxMinutes, totalDuration]
  );

  // Категории и фильтр товаров (как у услуг)
  const productCategories = [...new Set(products.map((p) => p.category?.name || noCategoryLabel))];
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      !productSearchQuery ||
      product.name.toLowerCase().includes(productSearchQuery.toLowerCase());
    const categoryName = product.category?.name || noCategoryLabel;
    const matchesCategory = selectedProductCategory === ALL_CATEGORY_KEY || categoryName === selectedProductCategory;
    return matchesSearch && matchesCategory;
  });

  if (!themeReady) {
    return null;
  }

  const isBookingDisabled =
    profile?.onlineBookingEnabled !== true ||
    profile?.enabled === false;

  if (!loading && profile && isBookingDisabled) {
    return (
      <div className="booking-container">
        <div className="booking-card">
          <div className="booking-card-lang-row">
            <LanguageSwitcher />
          </div>
          <div className="booking-p-md-lg" style={{ paddingTop: 'var(--spacing-xl)', textAlign: 'center', color: 'var(--color-text-primary)' }}>
            <h1 className="booking-heading booking-font-bold booking-mb-sm">{t('booking.off.title')}</h1>
            <p className="booking-color-muted" style={{ margin: 0 }}>{t('booking.off.subtitle')}</p>
          </div>
        </div>
        {summaryBar}
      </div>
    );
  }

  if (!loading && profileNotFound) {
    return (
      <div className="booking-container" style={{ textAlign: 'center' }}>
        <div className="booking-card">
          <div className="booking-card-lang-row">
            <LanguageSwitcher />
          </div>
          <p className="booking-404-code">404</p>
          <h1 className="booking-title">{t('booking.error.profileNotFound')}</h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: '0.5rem', fontSize: '0.9rem' }}>
            {t('booking.error.profileNotFoundHint')}
          </p>
        </div>
      </div>
    );
  }

  if (!slug) {
    return (
      <div className="booking-container">
        <div className="booking-card">
          <div className="booking-card-lang-row">
            <LanguageSwitcher />
          </div>
          <p className="booking-color-muted" style={{ textAlign: 'center', margin: 0 }}>
            {t('booking.error.slugRequired')}
          </p>
        </div>
      </div>
    );
  }

  // Форматирование часов работы
  const formatWorkHours = (profile: PublicProfile | null): string | null => {
    if (!profile || !profile.workHours || profile.workHours.length === 0) {
      return null;
    }

    const weekdayNames = [
      t('calendar.weekdays.sun').toLowerCase(),
      t('calendar.weekdays.mon').toLowerCase(),
      t('calendar.weekdays.tue').toLowerCase(),
      t('calendar.weekdays.wed').toLowerCase(),
      t('calendar.weekdays.thu').toLowerCase(),
      t('calendar.weekdays.fri').toLowerCase(),
      t('calendar.weekdays.sat').toLowerCase(),
    ];
    
    // Группируем дни с одинаковыми интервалами
    const intervalsByKey: Record<string, { weekdays: number[]; intervals: Array<{ start_min: number; end_min: number }> }> = {};
    
    profile.workHours.forEach(day => {
      const key = JSON.stringify(day.intervals);
      if (!intervalsByKey[key]) {
        intervalsByKey[key] = { weekdays: [], intervals: day.intervals };
      }
      intervalsByKey[key].weekdays.push(day.weekday);
    });

    const formatTime = (minutes: number): string => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    // Форматируем каждый блок
    const parts: string[] = [];
    Object.values(intervalsByKey).forEach(block => {
      if (block.intervals.length === 0) return;
      
      // Сортируем дни недели
      block.weekdays.sort((a, b) => a - b);
      
      // Формируем диапазон дней (пн-вс или отдельные дни)
      let dayRange = '';
      if (block.weekdays.length === 7) {
        dayRange = `${weekdayNames[1]}.-${weekdayNames[0]}.`;
      } else if (block.weekdays.length === 1) {
        dayRange = weekdayNames[block.weekdays[0]] + '.';
      } else {
        // Проверяем, непрерывный ли диапазон
        let isContinuous = true;
        for (let i = 1; i < block.weekdays.length; i++) {
          if (block.weekdays[i] !== block.weekdays[i - 1] + 1) {
            isContinuous = false;
            break;
          }
        }
        if (isContinuous) {
          dayRange = `${weekdayNames[block.weekdays[0]]}-${weekdayNames[block.weekdays[block.weekdays.length - 1]]}.`;
        } else {
          dayRange = block.weekdays.map(d => weekdayNames[d]).join(', ') + '.';
        }
      }

      // Форматируем интервалы времени
      const timeRanges = block.intervals.map(interval => 
        `${formatTime(interval.start_min)}-${formatTime(interval.end_min)}`
      ).join(', ');

      parts.push(`${dayRange} ${timeRanges}`);
    });

    return parts.length ? parts.join(';\n') : null;
  };

  const handleSelectServices = () => {
    setBookingFlowMode('services-first');
    setCurrentPage(1);
    setStep(1);
  };

  const handleSelectDateTime = () => {
    setBookingFlowMode('date-first');
    setCurrentPage(2);
    setStep(2);
    if (!selectedDate) {
      setSelectedDate(today);
    }
  };

  const handleSelectProducts = () => {
    setCurrentPage(5);
  };

  const toggleProduct = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const workHoursText = formatWorkHours(profile);
  const shouldShowExpand = !!(profile?.description && profile.description.length > UI_CONSTANTS.DESCRIPTION_PREVIEW_LENGTH);
  const displayDescription = descriptionExpanded 
    ? (profile?.description || '') 
    : (profile?.description?.substring(0, UI_CONSTANTS.DESCRIPTION_PREVIEW_LENGTH) || '');
  
  // Единый источник правды: только из profile (данные из настроек онлайн-записи)
  // Без fallback значений — если пусто, показываем локализованный текст
  const emptyText = t('booking.notFilled');
  const displayTitle = (profile?.publicTitle?.trim()) || emptyText;
  const displayAddress = (profile?.address?.trim()) || emptyText;
  const displayWorkHours = workHoursText || emptyText;
  const hasPhone =
    profile?.phone != null && String(profile.phone).replace(/\s/g, '').length > 0;

  // Первая страница: вся информация и CTA
  if (currentPage === 0) {
    return (
      <div className="booking-landing-container">
      <div className="booking-landing-card booking-landing-card--landing">
          {/* Hero: фото слева (портрет), инфо справа */}
          <div className="booking-landing-hero">
            {/* Фото слева - вертикальное (портрет) */}
            <div className="booking-landing-photo">
              {coverReady && coverUrl ? (
                <img 
                  src={coverUrl} 
                  alt={profile?.publicTitle || emptyText} 
                />
              ) : (
                <div className="booking-landing-photo-placeholder">
                  <AppIcon name="image" size={48} tone="muted" />
                </div>
              )}
            </div>

            {/* Информация справа */}
            <div className="booking-landing-info">
              <div className="booking-landing-title-row">
                <h2 className="booking-landing-title">{displayTitle}</h2>
                <LanguageSwitcher />
              </div>

              {/* Контакты: сначала телефон, потом график, потом адрес */}
              <div className="booking-landing-contacts">
                <div className="booking-landing-info-row">
                  <AppIcon name="phone" size={18} tone="muted" className="booking-landing-info-row-icon" />
                  {hasPhone ? (
                    <a href={`tel:${profile?.phone}`} className="booking-landing-link">
                      {formatPhone(profile?.phone || '')}
                    </a>
                  ) : (
                    <span className="booking-landing-empty">{emptyText}</span>
                  )}
                </div>

                <div className="booking-landing-info-row">
                  <AppIcon name="timeOutline" size={18} tone="muted" className="booking-landing-info-row-icon" />
                  <span className="booking-landing-work-hours">{displayWorkHours}</span>
                </div>

                <div className="booking-landing-info-row">
                  <AppIcon name="location" size={18} tone="muted" className="booking-landing-info-row-icon" />
                  <span>{displayAddress}</span>
                </div>
                
                <div className="booking-landing-social">
                  {contactLinks.length > 0 ? contactLinks.map(({ contact, href, label, icon }) => (
                    <a
                      key={`${contact.platform}-${contact.value}`}
                      href={href || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`booking-landing-social-icon booking-landing-social-icon--${contact.platform}`}
                      title={label}
                      aria-label={label}
                    >
                      <AppIcon name={icon} size={24} style={{ color: socialBrandIconCssColor(contact.platform) }} />
                    </a>
                  )) : (
                    <span className="booking-landing-social-icon is-disabled" title={emptyText} aria-label={emptyText}>
                      <AppIcon name="link" size={24} tone="muted" />
                    </span>
                  )}
                </div>
              </div>
              {/* Описание */}
              {profile?.description && (
                <div className="booking-landing-description">
                  <p>{displayDescription}{!descriptionExpanded && shouldShowExpand && '...'}</p>
                  {shouldShowExpand && (
                    <button
                      type="button"
                      onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                      className="booking-landing-more"
                    >
                      {descriptionExpanded ? t('booking.collapse') : t('booking.more')} {descriptionExpanded ? '↑' : '↓'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Блок действий: услуги, товары (если есть), дата и время */}
          <div className="booking-landing-actions">
            <button
              onClick={handleSelectServices}
              className="booking-landing-action-row booking-landing-action-row--services"
            >
              <span className="booking-landing-action-content">
                <span className="booking-landing-action-icon booking-landing-action-icon--services">
                  <AppIcon name="services" size={24} style={{ color: 'var(--booking-accent-service-icon)' }} />
                </span>
                <span>{t('booking.selectServices')}</span>
              </span>
              <AppIcon name="chevronRight" size={24} tone="muted" />
            </button>
            {products.length > 0 && (
              <button
                onClick={handleSelectProducts}
                className="booking-landing-action-row booking-landing-action-row--products"
              >
                <span className="booking-landing-action-content">
                  <span className="booking-landing-action-icon booking-landing-action-icon--products">
                    <AppIcon name="tag" size={24} style={{ color: 'var(--booking-accent-product-icon)' }} />
                  </span>
                  <span>{t('booking.selectProducts')}</span>
                </span>
                <AppIcon name="chevronRight" size={24} tone="muted" />
              </button>
            )}
            <button
              onClick={handleSelectDateTime}
              className="booking-landing-action-row booking-landing-action-row--datetime"
            >
              <span className="booking-landing-action-content">
                <span className="booking-landing-action-icon booking-landing-action-icon--datetime">
                  <AppIcon name="calendarOutline" size={24} style={{ color: 'var(--booking-accent-appointment-icon)' }} />
                </span>
                <span>{t('booking.selectDateTime')}</span>
              </span>
              <AppIcon name="chevronRight" size={24} tone="muted" />
            </button>
          </div>
        </div>
        {summaryBar}
      </div>
    );
  }

  if (currentPage === 5) {
    return (
      <div className="booking-landing-container">
        <BookingProductsPage
          t={t}
          priceLocale={priceLocale}
          searchQuery={productSearchQuery}
          selectedCategory={selectedProductCategory}
          categories={productCategories}
          filteredProducts={filteredProducts}
          selectedProductIds={selectedProducts}
          onBack={() => setCurrentPage(0)}
          onSearchChange={setProductSearchQuery}
          onCategoryChange={setSelectedProductCategory}
          onToggleProduct={toggleProduct}
        />
        {summaryBar}
      </div>
    );
  }

  if (currentPage === 1 && step === 1) {
    return (
      <div className="booking-landing-container">
        <BookingStep1Services
          t={t}
          priceLocale={priceLocale}
          bookingFlowMode={bookingFlowMode}
          selectedSlot={selectedSlot}
          loading={loading}
          searchQuery={searchQuery}
          selectedCategory={selectedCategory}
          categories={categories}
          filteredServices={filteredServices}
          selectedServices={selectedServices}
          canAddService={canAddService}
          formatSlotTime={formatSlotTime}
          onBack={() => {
            if (bookingFlowMode === 'date-first' && selectedSlot) {
              setCurrentPage(2);
              setStep(2);
            } else {
              setCurrentPage(0);
              clearAllSelections();
            }
            setError(null);
          }}
          onSearchChange={setSearchQuery}
          onCategoryChange={setSelectedCategory}
          onToggleService={toggleService}
        />
        {summaryBar}
      </div>
    );
  }

  // Шаги 2-3 (дата/время, данные клиента) — на том же листе (booking-landing-card), что и лендинг
  return (
    <div className="booking-landing-container">
      <div className="booking-landing-card has-summary booking-card-header-unified">
        {error && (
          <div className="booking-alert booking-alert-error">
            {error}
          </div>
        )}

        {currentPage === 2 && step === 2 && (
          <BookingStep2Time
            t={t}
            calendarRevealed={calendarRevealed}
            calendarError={calendarError}
            calendarRequestId={calendarRequestId}
            calendarLoading={calendarLoading}
            availableDates={availableDates}
            occupancyByDate={occupancyByDate}
            selectedDate={selectedDate}
            today={today}
            maxDate={maxDate}
            slotsLoading={slotsLoading}
            profileLoading={loading}
            availableSlots={availableSlots}
            groupedSlots={groupedSlots}
            selectedSlot={selectedSlot}
            onBack={() => {
              setCurrentPage(0);
              clearAllSelections();
            }}
            onDateSelect={handleDateSelect}
            onSlotSelect={handleSlotSelect}
            onMonthChange={handleCalendarMonthChange}
            formatSlotTime={formatSlotTime}
          />
        )}

      {step === 3 && (
        <BookingStep3Client
          t={t}
          firstName={firstName}
          lastName={lastName}
          clientPhoneE164={clientPhoneE164}
          effectiveClientEmail={effectiveClientEmail}
          lockedClientEmail={lockedClientEmail}
          comment={comment}
          vkId={vkId}
          vkPhoto={vkPhoto}
          loadingVkData={loadingVkData}
          clientGender={clientGender}
          clientBirthDate={clientBirthDate}
          nameError={nameError}
          emailError={emailError}
          phoneWarning={phoneWarning}
          bookingPhoneTouched={bookingPhoneTouched}
          bookingPhoneCountry={bookingPhoneCountry}
          onBack={() => {
            if (previousPage === 1) {
              setCurrentPage(1);
              setStep(1);
              setSelectedDate('');
              setSelectedSlot(null);
            } else {
              setCurrentPage(2);
              setStep(2);
              setSelectedSlot(null);
            }
            setError(null);
          }}
          onSubmit={handleSubmit}
          onFirstNameChange={setFirstName}
          onLastNameChange={setLastName}
          onPhoneChange={setClientPhoneE164}
          onPhoneBlur={() => setBookingPhoneTouched(true)}
          onEmailChange={setClientEmail}
          onCommentChange={setComment}
          onVkIdChange={handleVkIdChange}
          onFetchVkData={handleFetchVkData}
          onGenderChange={setClientGender}
          onBirthDateChange={setClientBirthDate}
          onNameBlur={handleClientNameBlur}
          onEmailBlur={handleClientEmailBlur}
          onSplitName={handleSplitName}
          onClearNameError={() => setNameError(null)}
          onClearEmailError={() => setEmailError(null)}
        />
      )}

      {rateLimitRetryAfter !== null && (
        <BookingRateLimitModal
          retryAfterSec={rateLimitRetryAfter}
          t={t}
          onClose={() => setRateLimitRetryAfter(null)}
          onRetry={handleRetry}
        />
      )}
      </div>
      {summaryBar}
    </div>
  );
}
